import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  updateProfile,
  deleteUser,
  User as FirebaseUser,
  AuthError,
} from 'firebase/auth';
import {
  doc,
  setDoc,
  getDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { auth, db } from '../firebase';
import { User, AuthUser, RegisterFormData, LoginFormData } from '../types';
import { logger } from '../utils/logger';

export class AuthService {
  // Register new user
  static async register(formData: RegisterFormData): Promise<AuthUser> {
    try {
      const { email, password, displayName } = formData;

      // Create user with Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const firebaseUser = userCredential.user;

      try {
        // Update display name
        await updateProfile(firebaseUser, {
          displayName: displayName,
        });

        // Create user profile in Firestore
        await this.createUserProfile(firebaseUser, displayName);
      } catch (profileError) {
        // 註冊非原子問題的補償:profile 建立失敗時刪除剛建立的 auth 帳號,
        // 避免留下無 profile 的孤兒使用者
        logger.auth.error('Profile creation failed during registration, rolling back auth user', profileError);
        try {
          await deleteUser(firebaseUser);
        } catch (rollbackError) {
          logger.auth.error('Rollback failed — orphan auth user remains', rollbackError);
        }
        throw profileError;
      }

      return {
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        displayName: displayName,
        photoURL: firebaseUser.photoURL,
      };
    } catch (error) {
      logger.auth.error('User registration failed', error);
      throw this.handleAuthError(error as AuthError);
    }
  }

  // Login user
  static async login(formData: LoginFormData): Promise<AuthUser> {
    try {
      const { email, password } = formData;
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const firebaseUser = userCredential.user;
      
      // Update last login time
      await this.updateUserProfile(firebaseUser.uid, {
        lastLoginAt: new Date(),
      });
      
      return {
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        displayName: firebaseUser.displayName,
        photoURL: firebaseUser.photoURL,
      };
    } catch (error) {
      logger.auth.error('User login failed', error);
      throw this.handleAuthError(error as AuthError);
    }
  }

  // Login with Google
  static async loginWithGoogle(): Promise<AuthUser> {
    try {
      const provider = new GoogleAuthProvider();
      provider.addScope('email');
      provider.addScope('profile');
      
      const userCredential = await signInWithPopup(auth, provider);
      const firebaseUser = userCredential.user;
      
      // Check if user profile exists, if not create one
      const existingProfile = await this.getUserProfile(firebaseUser.uid);
      if (!existingProfile) {
        await this.createUserProfile(firebaseUser, firebaseUser.displayName || 'User');
      } else {
        // Update last login time
        await this.updateUserProfile(firebaseUser.uid, {
          lastLoginAt: new Date(),
        });
      }
      
      return {
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        displayName: firebaseUser.displayName,
        photoURL: firebaseUser.photoURL,
      };
    } catch (error) {
      logger.auth.error('Google login failed', error);
      throw this.handleAuthError(error as AuthError);
    }
  }

  // Logout user
  static async logout(): Promise<void> {
    try {
      await signOut(auth);
    } catch (error) {
      logger.auth.error('User logout failed', error);
      throw this.handleAuthError(error as AuthError);
    }
  }

  // Create user profile in Firestore
  private static async createUserProfile(
    firebaseUser: FirebaseUser, 
    displayName: string
  ): Promise<void> {
    const userRef = doc(db, 'users', firebaseUser.uid);
    
    const userData: Omit<User, 'id'> = {
      name: displayName,
      email: firebaseUser.email || '',
      displayName: displayName,
      photoURL: firebaseUser.photoURL || '',
      createdAt: new Date(),
      lastLoginAt: new Date(),
    };
    
    await setDoc(userRef, {
      ...userData,
      createdAt: serverTimestamp(),
      lastLoginAt: serverTimestamp(),
    });
    // 注意:不再為 uid 建立 portfolio/watchlist(舊單人模式殘留)。
    // 多帳戶模型下,portfolios/watchlists 以 tradingAccounts ID 為文件 ID,
    // 於 admin 授權帳號後由對應 service 建立。
  }

  // Update user profile
  private static async updateUserProfile(
    userId: string,
    updates: Partial<User>
  ): Promise<void> {
    const userRef = doc(db, 'users', userId);
    const updateData: Record<string, unknown> = {
      ...updates,
    };

    if (updates.lastLoginAt) {
      updateData.lastLoginAt = serverTimestamp();
    }

    await setDoc(userRef, updateData, { merge: true });
  }

  // Get user profile from Firestore
  static async getUserProfile(userId: string): Promise<User | null> {
    try {
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        const data = userDoc.data();
        return {
          id: userId,
          ...data,
          createdAt: data.createdAt?.toDate(),
          lastLoginAt: data.lastLoginAt?.toDate(),
        } as User;
      }
      
      return null;
    } catch (error) {
      logger.auth.error('Failed to get user profile', error);
      return null;
    }
  }

  // Convert Firebase user to AuthUser
  static mapFirebaseUser(firebaseUser: FirebaseUser | null): AuthUser | null {
    if (!firebaseUser) return null;
    
    return {
      uid: firebaseUser.uid,
      email: firebaseUser.email,
      displayName: firebaseUser.displayName,
      photoURL: firebaseUser.photoURL,
    };
  }

  // Handle Firebase Auth errors
  private static handleAuthError(error: AuthError): Error {
    let message = 'Authentication failed';
    
    switch (error.code) {
      case 'auth/user-not-found':
        message = '找不到此帳號，請檢查電子郵件地址';
        break;
      case 'auth/wrong-password':
        message = '密碼錯誤，請重新輸入';
        break;
      case 'auth/email-already-in-use':
        message = '此電子郵件已被使用';
        break;
      case 'auth/weak-password':
        message = '密碼強度不足，至少需要6個字符';
        break;
      case 'auth/invalid-email':
        message = '電子郵件格式無效';
        break;
      case 'auth/too-many-requests':
        message = '嘗試次數過多，請稍後再試';
        break;
      case 'auth/network-request-failed':
        message = '網路連接失敗，請檢查網路連線';
        break;
      default:
        message = error.message || '驗證失敗';
        break;
    }
    
    return new Error(message);
  }
}
